/**
 * useMaterialsAdmin — выпуск «Вестника» (журнал клиентского приложения).
 * Только OO (perm actor_approval). Хранится в app_config, читается/пишется
 * через /api/oo/materials; источник правды — бэкенд getMaterialsIssue.
 *
 * Backend:
 *  - GET /api/oo/materials            → MaterialsIssue
 *  - PUT /api/oo/materials  body MaterialsIssue → { ok, issue: MaterialsIssue }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

export type ObjClass = "safe" | "euclid" | "keter" | "thaumiel";

export interface MaterialsCover {
  title: string;
  heroMediaUrl?: string;
}
export interface MaterialsEditor {
  author: string;
  text: string;
}
export interface MaterialsHoroscope {
  sign: string;
  questId: string;
  line: string;
}
export interface MaterialsAfisha {
  date: string;
  title: string;
  note: string;
}
export interface MaterialsReviewLinks {
  twogis?: string;
  yandex?: string;
  tg?: string;
}
export interface MaterialsReview {
  text: string;
  rewardUc: number;
  links: MaterialsReviewLinks;
}
export interface MaterialsObject {
  questId: string;
  name: string;
  objClass: ObjClass;
  blurb: string;
  coverUrl?: string;
}
export interface MaterialsProtocol {
  code: string;
  title: string;
  terms: string;
}
export interface MaterialsIssue {
  number: number;
  month: string;
  objectId: string;
  objClass: ObjClass;
  cover: MaterialsCover;
  editor: MaterialsEditor;
  horoscope: MaterialsHoroscope[];
  afisha: MaterialsAfisha[];
  review: MaterialsReview;
  objects: MaterialsObject[];
  protocols: MaterialsProtocol[];
}

export interface UseMaterialsAdminResult {
  issue: MaterialsIssue | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  save: (next: MaterialsIssue) => Promise<{ ok: boolean; error?: string; issue?: MaterialsIssue }>;
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : "Ошибка";
}

export function useMaterialsAdmin(): UseMaterialsAdminResult {
  const [issue, setIssue] = useState<MaterialsIssue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api<MaterialsIssue>("/api/oo/materials", { signal: ac.signal });
        if (ac.signal.aborted) return;
        setIssue(r);
        setLoading(false);
      } catch (e: unknown) {
        if (ac.signal.aborted) return;
        setError(errMsg(e));
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [trigger]);

  const save = useCallback(async (next: MaterialsIssue) => {
    try {
      const r = await api<{ ok: boolean; issue: MaterialsIssue }>("/api/oo/materials", { method: "PUT", body: next });
      if (r?.issue) setIssue(r.issue);
      return { ok: true, issue: r?.issue };
    } catch (e: unknown) {
      return { ok: false, error: errMsg(e) };
    }
  }, []);

  return { issue, loading, error, reload, save };
}
